/**
 * Facility API Service
 * Manages facility data using local storage
 */

import LocalStorageService from '../storage/LocalStorageService';
import { createFacility, calculateFacilityFeatures } from '../../data/models/Facility';
import { facilitiesData } from '../../data/facilitiesData';

class FacilityService {
  constructor() {
    this.storageKey = 'facilities';
    this.initialized = false;
  }

  /**
   * Remove duplicate facilities (keeps the first occurrence of each ID)
   */
  async removeDuplicates() {
    try {
      const facilities = LocalStorageService.get(this.storageKey, []);
      const seen = new Set();
      const unique = [];
      const duplicates = [];

      for (const facility of facilities) {
        if (facility.id && !seen.has(facility.id)) {
          seen.add(facility.id);
          unique.push(facility);
        } else {
          duplicates.push(facility.id || 'unknown');
        }
      }

      if (duplicates.length > 0) {
        console.log(`Removing ${duplicates.length} duplicate facilities:`, duplicates);
        LocalStorageService.set(this.storageKey, unique);
        return { success: true, removed: duplicates.length, kept: unique.length };
      }

      return { success: true, removed: 0, kept: unique.length };
    } catch (error) {
      console.error('Error removing duplicates:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Initialize facilities with sample data if empty
   */
  async initialize() {
    if (this.initialized) {
      // Still check for duplicates even if already initialized
      await this.removeDuplicates();
      return { success: true };
    }

    try {
      // First, remove any duplicates
      await this.removeDuplicates();
      
      const existing = LocalStorageService.get(this.storageKey, []);
      const existingIds = new Set(existing.map(f => f.id));
      
      // Only add facilities that don't already exist
      if (existing.length === 0 || existing.length < facilitiesData.length) {
        console.log('Initializing facilities with sample data...');
        const initializedFacilities = [];
        
        for (const facility of facilitiesData) {
          try {
            // Skip if facility already exists
            if (existingIds.has(facility.id)) {
              console.log(`Facility ${facility.id} already exists, skipping...`);
              continue;
            }

            // Calculate features based on facility types
            const facilityWithFeatures = {
              ...facility,
              features: calculateFacilityFeatures(facility)
            };
            
            const added = LocalStorageService.addItem(this.storageKey, facilityWithFeatures);
            initializedFacilities.push(added);
          } catch (facilityError) {
            console.error(`Error initializing facility ${facility.id}:`, facilityError);
            // Continue with other facilities even if one fails
          }
        }
        console.log(`Initialized ${initializedFacilities.length} new facilities`);
      }
      
      // Final cleanup to ensure no duplicates
      await this.removeDuplicates();
      
      this.initialized = true;
      return { success: true };
    } catch (error) {
      console.error('Error initializing facilities:', error);
      this.initialized = true; // Set to true to prevent infinite retries
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all facilities
   */
  async getAll() {
    try {
      // Initialize if not already done
      await this.initialize();
      
      // Remove duplicates before returning
      await this.removeDuplicates();
      
      const facilities = LocalStorageService.get(this.storageKey, []);
      return { success: true, data: facilities };
    } catch (error) {
      console.error('Error getting all facilities:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get facility by ID
   */
  async getById(facilityId) {
    try {
      // Initialize if not already done
      await this.initialize();
      
      const facility = LocalStorageService.findById(this.storageKey, facilityId);
      
      if (!facility) {
        return { success: false, error: 'Facility not found' };
      }
      
      return { success: true, data: facility };
    } catch (error) {
      console.error('Error getting facility by ID:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get facilities by business group
   */
  async getByBusinessGroup(businessGroupId) {
    try {
      await this.initialize();
      const facilities = LocalStorageService.find(
        this.storageKey,
        f => f.businessGroup?.id === businessGroupId
      );
      
      return { success: true, data: facilities };
    } catch (error) {
      console.error('Error getting facilities by business group:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get facilities by type
   */
  async getByType(type) {
    try {
      await this.initialize();
      const facilities = LocalStorageService.find(
        this.storageKey,
        f => f.facilityTypes.includes(type) || f.primaryType === type
      );
      
      return { success: true, data: facilities };
    } catch (error) {
      console.error('Error getting facilities by type:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create facility
   */
  async create(facilityData) {
    try {
      const facility = createFacility(facilityData);
      
      // Calculate features based on facility types
      facility.features = calculateFacilityFeatures(facility);
      
      // Determine system context
      if (facility.primaryType === 'ndis') {
        facility.systemContext = 'ndis';
      } else if (facility.primaryType === 'agedcare') {
        facility.systemContext = 'agedcare';
      } else if (facility.primaryType === 'dual') {
        facility.systemContext = 'agedcare'; // Default
      }
      
      const created = LocalStorageService.addItem(this.storageKey, facility);
      
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Update facility
   */
  async update(facilityId, updates) {
    try {
      await this.initialize();
      const existing = LocalStorageService.findById(this.storageKey, facilityId);
      
      if (!existing) {
        return { success: false, error: 'Facility not found' };
      }
      
      const updated = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      // Recalculate features if types changed
      if (updates.facilityTypes || updates.ndis?.enabled || updates.agedCare?.enabled) {
        updated.features = calculateFacilityFeatures(updated);
      }
      
      const result = LocalStorageService.updateItem(this.storageKey, facilityId, updated);
      
      return { success: true, data: result };
    } catch (error) {
      console.error('Error updating facility:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete facility
   */
  async delete(facilityId) {
    try {
      await this.initialize();
      const deleted = LocalStorageService.removeItem(this.storageKey, facilityId);
      return { success: deleted };
    } catch (error) {
      console.error('Error deleting facility:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get business groups
   */
  async getBusinessGroups() {
    try {
      await this.initialize();
      const facilities = LocalStorageService.get(this.storageKey, []);
      const groups = {};
      
      facilities.forEach(facility => {
        if (facility.businessGroup?.id) {
          const groupId = facility.businessGroup.id;
          if (!groups[groupId]) {
            groups[groupId] = {
              id: groupId,
              name: facility.businessGroup.name,
              abn: facility.businessGroup.abn,
              corporateStructure: facility.businessGroup.corporateStructure,
              parentCompany: facility.businessGroup.parentCompany,
              facilities: []
            };
          }
          groups[groupId].facilities.push(facility);
        }
      });
      
      return { success: true, data: Object.values(groups) };
    } catch (error) {
      console.error('Error getting business groups:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new FacilityService();

